import i18n from "./i18n";
import { format as dateFnsFormat } from "date-fns";
import { enGB } from "date-fns/locale/en-GB";

const localeFromModule = (mod, key) => mod?.[key] || mod?.default || mod;

// Cache loaded locales
const localeCache = { en: enGB };

// Dynamic importers for each supported language
const localeImporters = {
  ar: () => import("date-fns/locale/ar").then((mod) => localeFromModule(mod, "ar")),
  bn: () => import("date-fns/locale/bn").then((mod) => localeFromModule(mod, "bn")),
  es: () => import("date-fns/locale/es").then((mod) => localeFromModule(mod, "es")),
  fr: () => import("date-fns/locale/fr").then((mod) => localeFromModule(mod, "fr")),
  gd: () => import("date-fns/locale/gd").then((mod) => localeFromModule(mod, "gd")),
  gu: () => import("date-fns/locale/gu").then((mod) => localeFromModule(mod, "gu")),
  pl: () => import("date-fns/locale/pl").then((mod) => localeFromModule(mod, "pl")),
  pt: () => import("date-fns/locale/pt").then((mod) => localeFromModule(mod, "pt")),
  ro: () => import("date-fns/locale/ro").then((mod) => localeFromModule(mod, "ro")),
  tr: () => import("date-fns/locale/tr").then((mod) => localeFromModule(mod, "tr")),
  // pa (Punjabi), ur (Urdu), so (Somali) not in date-fns -- fall back to enGB
};

/**
 * Get the current date-fns locale (synchronous, uses cache or fallback).
 */
const getDateLocaleSync = () => {
  const lang = i18n.language || "en";
  return localeCache[lang] || enGB;
};

/**
 * Preload the date-fns locale for the current language (async).
 * Call this when the user switches language.
 */
const preloadDateLocale = async () => {
  const lang = i18n.language || "en";
  if (localeCache[lang]) return;
  const importer = localeImporters[lang];
  if (!importer) return;
  try {
    localeCache[lang] = await importer();
  } catch {
    // Locale not available, will use enGB fallback
  }
};

/**
 * Format a date using the current i18n locale.
 * Uses "P" (locale-aware long date) by default, replacing hardcoded "dd/MM/yyyy".
 * Use "Pp" for date + time, or "p" for time only.
 */
const formatDate = (date, formatStr) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "";
  const locale = getDateLocaleSync();
  return dateFnsFormat(d, formatStr || "P", { locale });
};

/**
 * Format a date with time using the current i18n locale.
 */
const formatDateTime = (date) => {
  return formatDate(date, "Pp");
};

// Preload on init if non-English
if (i18n.language && i18n.language !== "en") {
  preloadDateLocale();
}

// Preload when language changes
i18n.on("languageChanged", () => {
  preloadDateLocale();
});

export { formatDate, formatDateTime, preloadDateLocale, getDateLocaleSync };
